import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { MaskLines } from "./Motion";
import { SITE, waLink } from "../lib/site";
import { scrollToId } from "./SmoothScroll";

const EASE = [0.22, 1, 0.36, 1];

const TiltMonogram = ({ y }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });
  const lx = useTransform(mx, [-0.5, 0.5], ["20%", "80%"]);
  const ly = useTransform(my, [-0.5, 0.5], ["20%", "80%"]);
  const light = useTransform([lx, ly], ([a, b]) => `radial-gradient(420px circle at ${a} ${b}, rgba(252,246,186,0.28), transparent 60%)`);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <motion.div style={{ y }} className="relative [perspective:1200px]" onMouseMove={onMove} onMouseLeave={reset} data-testid="hero-monogram">
      <motion.div
        initial={{ opacity: 0, scale: 0.92, clipPath: "inset(50% 0 50% 0)" }}
        animate={{ opacity: 1, scale: 1, clipPath: "inset(0% 0 0% 0)" }}
        transition={{ duration: 1.6, ease: EASE, delay: 0.5 }}
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="relative aspect-[4/5] w-full overflow-hidden border border-[#D4AF37]/30 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.8)]"
      >
        <img src="/img/monogram.webp" alt="Monograma Grupo Augustinis" className="absolute inset-0 h-full w-full object-cover scale-[1.15]" />
        <motion.span style={{ background: light }} className="absolute inset-0 mix-blend-screen" />
        <span className="absolute inset-0 hero-sheen" />
        <span className="absolute inset-4 border border-[#D4AF37]/25 pointer-events-none" />
        <span className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-[10px] tracking-[0.35em] text-[#E6C687]">
          <span>DIREITO E SOLUÇÕES</span>
          <span className="font-mono tracking-normal">RJ · BR</span>
        </span>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden pt-32 pb-20 lg:pt-40">
      <div className="absolute inset-0 hero-bg" />
      <div className="absolute inset-0 grid-lines opacity-40" />
      <motion.span initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 1.8, ease: EASE }} className="absolute left-0 right-0 top-[88px] h-px bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent origin-left" />
      <div className="relative mx-auto max-w-[1360px] px-6 lg:px-10 grid gap-14 lg:grid-cols-12 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.9, delay: 0.2 }} className="flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold">
            <span className="h-px w-10 bg-[#D4AF37]" /> Negociação bancária & defesa patrimonial
          </motion.p>
          <MaskLines
            testid="hero-title"
            lines={["Seu patrimônio", "merece uma defesa", "à altura."]}
            delay={0.35}
            className="mt-8 font-display text-[44px] leading-[0.98] sm:text-6xl lg:text-[88px] text-white tracking-[-0.01em]"
          />
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.1 }} className="mt-8 max-w-xl text-base md:text-lg text-[#C0C9DC] leading-relaxed">
            Negociamos com os bancos e defendemos quem enfrenta busca e apreensão de veículos, máquinas agrícolas e outros bens em alienação fiduciária — além de execuções de título extrajudicial e ações monitórias.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.25 }} className="mt-10 flex flex-col sm:flex-row gap-4">
            <a href={waLink()} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn" className="btn-gold justify-center"><MessageCircle className="w-4 h-4" /> Falar no WhatsApp</a>
            <button onClick={() => scrollToId("perfis")} data-testid="hero-profiles-btn" className="btn-ghost-gold justify-center">Encontre o seu caso <ArrowDown className="w-4 h-4" /></button>
          </motion.div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }} className="mt-14 font-cinzel text-xs sm:text-sm tracking-[0.18em] text-[#E6C687]/90">{SITE.tagline}</motion.p>
        </motion.div>
        <div className="lg:col-span-5 max-w-[420px] w-full mx-auto lg:max-w-none">
          <TiltMonogram y={y} />
        </div>
      </div>
    </section>
  );
};
